import { useState } from "react";
import { Shield } from "lucide-react";
import { MmcFrame } from "../../admin-framework/MmcFrame.js";
import type { SimulatorAppSurfaceProps } from "../AppSurface.js";

const firewallRows = [
  { ruleName: "Remote Access TCP-In", group: "Remote Access", profile: "Domain", enabled: "Yes", action: "Allow", port: "3389" },
  { ruleName: "File Sharing SMB-In", group: "File Sharing", profile: "Domain", enabled: "No", action: "Allow", port: "445" },
  { ruleName: "Remote Management HTTP-In", group: "Remote Management", profile: "Domain", enabled: "Yes", action: "Allow", port: "5985" }
];

export function FirewallConsoleApp({ activeMachine, onRunCommand }: SimulatorAppSurfaceProps) {
  const [ruleName, setRuleName] = useState("Remote Access TCP-In");
  const [profileName, setProfileName] = useState("domain");
  const [port, setPort] = useState("3389");

  async function setProfile(enabled: boolean) {
    await onRunCommand({
      appId: "wf",
      commandType: "FIREWALL_SET_PROFILE",
      capability: "firewall.profile.write",
      label: `${enabled ? "Enable" : "Disable"} ${profileName}`,
      payload: { enabled, machineId: activeMachine, profileName }
    });
  }

  async function setRule(
    commandType: "FIREWALL_ENABLE_RULE" | "FIREWALL_DISABLE_RULE",
    nextRuleName = ruleName,
    nextPort = port
  ) {
    await onRunCommand({
      appId: "wf",
      commandType,
      capability: "firewall.rule.write",
      label: `${commandType} ${nextRuleName}`,
      payload: {
        direction: "in",
        machineId: activeMachine,
        port: Number(nextPort),
        profileName,
        protocol: "TCP",
        ruleName: nextRuleName
      }
    });
  }

  return (
    <div className="fd-sim-app fd-sim-mmc">
      <MmcFrame
        actions={[
          { label: "New Rule", onClick: () => void setRule("FIREWALL_ENABLE_RULE") },
          { label: "Enable Rule", onClick: () => void setRule("FIREWALL_ENABLE_RULE") },
          { label: "Disable Rule", onClick: () => void setRule("FIREWALL_DISABLE_RULE") },
          { label: "Profile", onClick: () => void setProfile(true) }
        ]}
        columns={[
          { key: "name", label: "Name" },
          { key: "group", label: "Group" },
          { key: "profile", label: "Profile" },
          { key: "enabled", label: "Enabled" },
          { key: "action", label: "Action" },
          { key: "port", label: "Port" }
        ]}
        rows={firewallRows.map((row) => ({
          id: row.ruleName,
          onDoubleClick: () => {
            setRuleName(row.ruleName);
            setPort(row.port);
            void setRule(row.enabled === "Yes" ? "FIREWALL_DISABLE_RULE" : "FIREWALL_ENABLE_RULE", row.ruleName, row.port);
          },
          values: {
            action: row.action,
            enabled: row.enabled,
            group: row.group,
            name: <button className="fd-mmc-link" type="button" onClick={() => { setRuleName(row.ruleName); setPort(row.port); }}>{row.ruleName}</button>,
            port: row.port,
            profile: row.profile
          }
        }))}
        title="Firewall Console"
        tree={[
          { active: true, icon: <Shield size={15} />, id: "root", label: `Firewall (${activeMachine})` },
          { id: "inbound", label: "Inbound Rules" },
          { id: "outbound", label: "Outbound Rules" },
          { id: "monitoring", label: "Monitoring" }
        ]}
      >
        <section className="fd-mmc-detail-panel">
          <header>
            <div>
              <p>Selected rule</p>
              <h3>{ruleName}</h3>
            </div>
            <button type="button" onClick={() => void setProfile(true)}>Enable profile</button>
          </header>
          <div className="fd-command-form">
            <label>
              Rule name
              <input value={ruleName} onChange={(event) => setRuleName(event.target.value)} />
            </label>
            <label>
              Profile
              <input value={profileName} onChange={(event) => setProfileName(event.target.value)} />
            </label>
            <label>
              TCP port
              <input value={port} onChange={(event) => setPort(event.target.value)} />
            </label>
            <button type="button" onClick={() => void setRule("FIREWALL_ENABLE_RULE")}>Enable rule</button>
            <button type="button" onClick={() => void setRule("FIREWALL_DISABLE_RULE")}>Disable rule</button>
          </div>
        </section>
      </MmcFrame>
    </div>
  );
}
